import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";

import { Badge } from "@/components/ui/badge";
import ScoreBadge from "../../components/score-badge";
import type { Insight } from "../../entities/insights";

/*
 * Columns for the insights table.
 *
 * Everything renders as plain text. React escapes by default, and these strings
 * are LLM output derived from media that users upload — rendering any of it as
 * markdown or through dangerouslySetInnerHTML would turn an uploaded image
 * caption into stored XSS. If someone later asks for rich recommendations, the
 * answer is a sanitiser, not a renderer.
 *
 * WIDTH: `<Table>` uses auto layout inside an `overflow-x-auto` container, and
 * DataTable does not apply TanStack's `size` to cells — so a column grows to fit
 * whatever it contains and pushes the rest off-screen. A description is 2-4
 * sentences, which was enough to require horizontal scrolling to see the status
 * column at all.
 *
 * `truncate` alone does nothing here: it needs a width to truncate against.
 * Hence the explicit max-widths below. They step up with the viewport so a wide
 * screen shows more of the sentence rather than capping everyone at the
 * narrowest useful size. Everything else is `whitespace-nowrap`, so the flexible
 * text column is the only one that can absorb the remaining space.
 */

/*
 * Status badge variants.
 *
 * `open` is rendered rather than left blank. It is the default filter, so most
 * of the time every row carries the same badge — but an empty cell under a
 * "Status" header reads as missing data, not as "nothing has happened yet".
 * Outline keeps it present without competing with the rows that were acted on.
 *
 * `validated` is NOT green. Green already means "informational, no urgency" in
 * the severity column right next to it, and a confirmed problem is close to the
 * opposite. A red insight showing a green status badge is exactly the kind of
 * thing people misread at a glance.
 */
const STATUS_VARIANT: Record<string, "outline" | "default" | "secondary"> = {
  open: "outline",
  validated: "default",
  dismissed: "secondary",
};

interface Options {
  t: TFunction;
  language?: string;
}

export default function generateColumns({ t, language }: Options): ColumnDef<Insight>[] {
  return [
    {
      accessorKey: "score",
      header: () => t("insights.columns.score", "Severity"),
      cell: ({ row }) => <ScoreBadge score={row.original.score} />,
    },
    {
      accessorKey: "title",
      header: () => t("insights.columns.title", "Insight"),
      cell: ({ row }) => (
        // The cap lives on the wrapper, not the cell: the <td> then cannot grow
        // past it, which is what makes `truncate` engage at all.
        <div className="max-w-[16rem] md:max-w-[22rem] xl:max-w-[32rem] 2xl:max-w-[44rem]">
          {/* `title` so the full text is still reachable on hover — the row
              opens the drawer on click, but a tooltip costs nothing. */}
          <div className="truncate font-medium" title={row.original.title}>
            {row.original.title}
          </div>
          <div
            className="truncate text-sm text-muted-foreground"
            title={row.original.description}
          >
            {row.original.description}
          </div>
        </div>
      ),
    },
    {
      id: "coverage",
      header: () => t("insights.columns.coverage", "Coverage"),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {t("insights.coverage", {
            defaultValue: "{{resources}} resources · {{inspections}} inspections",
            resources: row.original.resourceCount ?? 0,
            inspections: row.original.inspectionCount ?? 0,
          })}
        </span>
      ),
    },
    {
      accessorKey: "confidence",
      header: () => t("insights.columns.confidence", "Confidence"),
      cell: ({ row }) => (
        <Badge variant="outline" className="whitespace-nowrap">
          {new Intl.NumberFormat(language, { style: "percent" }).format(
            row.original.confidence ?? 0,
          )}
        </Badge>
      ),
    },
    {
      accessorKey: "status",
      header: () => t("insights.columns.status", "Status"),
      cell: ({ row }) => {
        const status = row.original.status ?? "open";
        return (
          <Badge variant={STATUS_VARIANT[status] ?? "outline"} className="whitespace-nowrap">
            {t(`insights.status.${status}`, status)}
          </Badge>
        );
      },
    },
  ];
}
